/**
 * The frame every onboarding step sits in: the brand linking home, which step this is, the step's
 * own column, and on wide screens a side panel saying what happens after this step and when the
 * card is charged. A visitor from an ad should always know how much is left and what it costs.
 */
import * as stylex from "@stylexjs/stylex";
import type { ReactNode } from "react";
import { useEffect } from "react";
import "@lattice/tokens/color.stylex";
import { color } from "@lattice/tokens/color.stylex";
import "@lattice/tokens/space.stylex";
import { radius, space } from "@lattice/tokens/space.stylex";
import "@lattice/tokens/text.stylex";
import { text } from "@lattice/tokens/text.stylex";
import { focusRing } from "@lattice/ui/focus";
import { Mark } from "@lattice/ui/Mark";
const WIDE = "@media (min-width: 960px)";

/* The step column is about 70 characters of body text, the longest comfortable line. */
const COLUMN = "34rem";
const ASIDE = "19rem";
const styles = {
  skip: {
    kVAEAm: "x10l6tqk",
    k87sOh: "xhwcp2v",
    kLqNvP: "x1eua9zb",
    k8WAf4: "x1jyo4x1",
    kg3NbH: "x3i8z2n",
    kaIpWk: "x1fbwzx",
    kWkggS: "xcqm4px",
    kMwMTN: "xzb3lse",
    k3aq6I: "xr996gy x1cq5w7s",
    $$css: true
  }
};
export function Shell({
  title,
  step,
  of,
  aside,
  children
}: {
  /** Names the browser tab, as "{title} · Lattice", so history says which step a visitor was on. */
  readonly title: string;
  readonly step: number;
  readonly of: number;
  /** Beside the step on wide screens, under it below 960px, so price and wait are never missing. */
  readonly aside?: ReactNode;
  readonly children: ReactNode;
}) {
  useEffect(() => {
    document.title = `${title} · Lattice`;
  }, [title]);
  return <div className="x1ov3xa9 x78zum5 xdt5ytf x1l0w1cl xzb3lse x1cikjda x1rf3zrh xbz0q3o xelt9ym">
      <a href="#content" {...stylex.props(styles.skip, focusRing)}>
        Skip to content
      </a>
      <div className="x1iyjqo2 xh8yej3 x19pysoa xyh2l3g xvueqy4 xcmmui4">
        <header className="x78zum5 x6s0dn4 x1qughib x1r0x45p x1wiri7k">
          <a href="/" className="x78zum5 x6s0dn4 x1173whf x1gg8mnh x10dbofl x1hl2dhg xqu410z x8wwnw1 x2jzzdg x1ewu8gn x1de99jn x1t137rt x9v5kkp" aria-label="Lattice home">
            <Mark size={22} />
            <span className="xzb3lse">Lattice</span>
          </a>
          <span className="xl4dl4e x1jty25b x1l1vl4p">
            Step {step} of {of}
          </span>
        </header>
        <div className="xrvj5dj x1mkdm3x x1lenqxw x1qughib x7a106z x1b3r8ps x8nvr85">
          <main id="content" tabIndex={-1} className="x78zum5 xdt5ytf xd3upel xeuugli">
            {children}
          </main>
          {aside && <aside className="x78zum5 x1uhb9sk x17tt5qk x13fi8xg xdt5ytf x1r0x45p xq24xx7 x1hegsvj x1890um5 x197fguk xyk9i7q">{aside}</aside>}
        </div>
      </div>
    </div>;
}

/** Heading and lead for a step, sized and spaced the same everywhere in the flow. */
export const stepText = {
  block: {
    k1xSpc: "x78zum5",
    kXwgrk: "xdt5ytf",
    kOIVth: "x626il7",
    $$css: true
  },
  heading: {
    kogj98: "x1ghz6dp",
    kGuDYH: "x1u29x3t",
    k63SB2: "xqu410z",
    kQqvRs: "x1ntj4s9",
    kb6lSQ: "x1d3j7mx",
    kLWn49: "x1e5flnj",
    kN2L0X: "x1w2vvpw",
    $$css: true
  },
  subheading: {
    kogj98: "x1ghz6dp",
    kGuDYH: "x3cmmws",
    k63SB2: "xqu410z",
    kb6lSQ: "x1d3j7mx",
    kLWn49: "x1e5flnj",
    kN2L0X: "x1w2vvpw",
    $$css: true
  },
  lead: {
    kogj98: "x1ghz6dp",
    kMwMTN: "x1jty25b",
    kN2L0X: "x1fzhlzt",
    $$css: true
  },
  actions: {
    k1xSpc: "x78zum5",
    kXwgrk: "xdt5ytf",
    kOIVth: "x1173whf",
    kGNEyG: "x1qjc9v5",
    $$css: true
  }
};